import { toBool } from './utils';

// CP-857: the TenStreet code tables the BI Admin role maintains.
//
// One screen, two tables. Everything that differs between them lives here: fields, labels,
// endpoints, and the copy that explains what the table is for. The screen, the form, the
// import and the history dialog are written once and read this.
//
// Adding a third code table means adding an entry here plus a route and a menu item -- no
// new screen, and no new audit plumbing, because tb_cl_cp_bi_codetable_audit is shared.
//
// CENTERLINE ONLY. The data is TenStreet, which PRST does not have, so the routes and the
// Admin Portal buttons are brand-gated. See routes/index.js and AdminPortal/adminSlice.js.
//
// FIELD LABELS ARE A CONTRACT, not cosmetic. The stored procedures write these exact
// strings into tb_cl_cp_bi_codetable_audit.FieldName, so the change history names a field
// the same way the grid does. Renaming a label here without changing the proc makes the
// history disagree with the screen.

/**
 * @typedef {Object} CodeTableField
 * @property {string}  key       API/grid field name
 * @property {string}  label     what the user sees -- must match the proc's audit FieldName
 * @property {string}  type      'text' | 'number' | 'boolean'
 * @property {boolean} [required]
 * @property {boolean} [isKey]   part of the row's natural key; editing it re-keys the row
 * @property {string}  [hint]    full name behind an abbreviation, shown on hover
 */

export const CODE_TABLES = {
  ApplicantStatus: {
    type: 'ApplicantStatus',
    title: 'Tenstreet Applicant Status',
    // Sent to /code-table-audit; the API whitelists it.
    tableName: 'tb_cl_ApplicantStatus',
    endpoint: '/tenstreet-applicant-status',
    importEndpoint: '/tenstreet-applicant-status-import',
    keyField: 'TenStreetStatus',
    keyLabel: 'TenStreet Status',
    rowNoun: 'status',
    intro:
      'Defines which TenStreet statuses count as an applicant on the Centerline Weekly Report. ' +
      'It is data rather than a rule in a report, so changing it here needs no code release.',
    // The pick-list is a source of KNOWN values for the "never sent" warning, not a
    // constraint. The proc accepts an unseen status deliberately: one can be classified
    // before its first application arrives.
    pickList: {
      endpoint: '/tenstreet-status-list',
      valueKey: 'TenStreetStatus',
      usedKey: 'IsClassified',
    },
    /**
     * This is an OPT-IN list: a status absent from it simply does not count as an applicant,
     * which is the correct state for almost all of them. Of the 67 statuses TenStreet has
     * ever sent, 4 count — "Left Message" (1.2M rows), "No Contact" and "Disqualified" are
     * all absent, and all should be. So a count of what is NOT listed is not a backlog and
     * must not be presented as one; it would read as 63 pending decisions when the answer
     * is none. State the inclusion instead.
     */
    summarize: rows =>
      rows.length
        ? `${rows.filter(row => toBool(row?.IsClassified, false)).length} of ${rows.length} ` +
          'TenStreet statuses count as an applicant.'
        : '',
    unseenWarning: 'TenStreet has never sent this status, so it will not match any application yet.',
    deleteWarning:
      'Deleting is not the same as clearing "Counts As Applicant". The flag keeps the row here as a ' +
      'recorded decision; delete removes it entirely. The previous values stay in the change history.',
    fields: [
      { key: 'TenStreetStatus', label: 'TenStreet Status', type: 'text', required: true, isKey: true },
      { key: 'CountsAsApplicant', label: 'Counts As Applicant', type: 'boolean' },
      { key: 'Notes', label: 'Notes', type: 'text' },
    ],
  },

  WorklistXref: {
    type: 'WorklistXref',
    title: 'Tenstreet Worklist Xref',
    tableName: 'tb_cl_WorklistXref',
    endpoint: '/tenstreet-worklist-xref',
    importEndpoint: '/tenstreet-worklist-xref-import',
    keyField: 'Worklist',
    keyLabel: 'Worklist',
    rowNoun: 'worklist mapping',
    intro:
      'Maps a TenStreet worklist to a branch and cost center. Only the worklists that break the ' +
      '"Marietta, GA (810)" naming convention need a row here — the rest resolve from their own name.',
    pickList: {
      endpoint: '/tenstreet-worklist-list',
      valueKey: 'Worklist',
      usedKey: 'IsMapped',
    },
    /**
     * Also an EXCEPTIONS list — 87 of the 98 worklists resolve from their own
     * "Marietta, GA (810)" name and correctly have no row, so "unmapped" is not a backlog
     * either. The real gap is a worklist that resolves NEITHER way: no trailing (nnn) and
     * no xref row, which silently drops branch attribution on every application under it.
     * That is the number worth showing, and saying so when it is zero is the reassurance
     * this screen exists to give.
     */
    summarize: rows => {
      if (!rows.length) return '';
      const unresolved = rows.filter(
        row => !toBool(row?.IsMapped, false) && !toBool(row?.ParsesToBranch, false),
      ).length;
      return unresolved
        ? `${unresolved} ${unresolved === 1 ? 'worklist resolves' : 'worklists resolve'} to no branch — ` +
            'no trailing (nnn) in the name and no mapping here.'
        : 'Every worklist TenStreet has sent resolves to a branch, by name or by mapping.';
    },
    unseenWarning: 'TenStreet has never sent this worklist, so the mapping will not match any application.',
    // Deliberately blunt. IsActive = 0 is not a soft delete: TenStreet renamed the West
    // Virginia worklist in August but 10,774 historical rows keep the bare name, so removing
    // a mapping blanks branch attribution on all of them.
    deleteWarning:
      'Deleting a mapping blanks the branch on every historical TenStreet row that still carries this ' +
      'worklist name. To retire a mapping, clear "Active" instead — that keeps it working for history.',
    fields: [
      { key: 'Worklist', label: 'Worklist', type: 'text', required: true, isKey: true },
      // Blank is meaningful on both: DMS worklists resolve to one cost center across all
      // branches, and the dead worklists are deliberately unmapped.
      { key: 'BranchNo', label: 'Branch No', type: 'number' },
      { key: 'CostCenter', label: 'Cost Center', type: 'number' },
      { key: 'IsActive', label: 'Active', type: 'boolean' },
      { key: 'Notes', label: 'Notes', type: 'text' },
    ],
  },
};

export const configFor = type => CODE_TABLES[type];
